import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useRef, useState } from 'react';
import { FieldErrors, useForm, useWatch } from 'react-hook-form';
import { cn } from '../../../../lib/utils/cn';
import { Button } from '../../../../ui/components/ui/button';
import { Form } from '../../../../ui/components/ui/form';
import { CheckoutSection } from '../../constants/checkout-section.enum';
import { PAYMENT_METHOD_LABEL } from '../../constants/payment-method';
import { PaymentMethod } from '../../constants/payment-method.enum';
import { compactBankValue, getCountryLabel, maskIban } from '../../lib/format';
import { AcceptOfferPayload } from '../../types/request';
import { BankDetailsValidationResponseDto, OfferCheckoutCustomerResponseDto } from '../../types/response';
import CheckoutDataRow from '../checkout-data-row/checkout-data-row';
import CustomerContactFields from '../customer-contact-fields/customer-contact-fields';
import CustomerDataFields from '../customer-data-fields/customer-data-fields';
import SepaFields from '../sepa-fields/sepa-fields';
import SepaMandateCheckbox from '../sepa-mandate-checkbox/sepa-mandate-checkbox';
import StartDateSelect from '../start-date-select/start-date-select';
import TermsCheckbox from '../terms-checkbox/terms-checkbox';
import {
  buildOfferCheckoutFormDefaults,
  createOfferCheckoutFormSchema,
  getIncompleteSections,
  OfferCheckoutFormValues,
  SECTION_FIELDS,
  sepaSectionSchema,
  toAcceptOfferRequest
} from './offer-checkout-form.schema';

export interface OfferCheckoutFormProps {
  customer: OfferCheckoutCustomerResponseDto;
  paymentMethod: PaymentMethod;
  startDateOptions: string[];
  /** Without it the form is a preview: everything can be tried out, but nothing can be submitted. */
  onSubmit?: (payload: AcceptOfferPayload) => Promise<void>;
  /** Live IBAN/BIC check (SEPA). `null` = the check could not run. Without it only the format is checked (preview). */
  validateBankDetails?: (iban: string, bic: string) => Promise<BankDetailsValidationResponseDto | null>;
}

interface BankCheck {
  /** The compacted IBAN + BIC the result belongs to. */
  key: string;
  result: BankDetailsValidationResponseDto;
}

const toBankKey = (iban: string, bic: string) => `${compactBankValue(iban)}|${compactBankValue(bic)}`;

/**
 * Right side of the checkout, also used as the admin live preview – props only, no requests.
 * Complete sections show as a summary with a pencil, incomplete ones open as inputs, so an empty prefill is simply a form.
 */
const OfferCheckoutForm = ({ customer, paymentMethod, startDateOptions, onSubmit, validateBankDetails }: OfferCheckoutFormProps) => {
  const isPreview = !onSubmit;
  const isSepa = paymentMethod === PaymentMethod.SEPA;

  const schema = useMemo(() => createOfferCheckoutFormSchema(paymentMethod), [paymentMethod]);
  const form = useForm<OfferCheckoutFormValues>({
    resolver: zodResolver(schema),
    // Errors update while typing once a field was touched – a stale error vanishing on blur would shift the layout under the next click.
    mode: 'onTouched',
    defaultValues: buildOfferCheckoutFormDefaults(customer, startDateOptions)
  });
  const values = useWatch({ control: form.control });

  const [bankCheck, setBankCheck] = useState<BankCheck | null>(null);
  const [isCheckingBank, setIsCheckingBank] = useState(false);
  // Leaving the IBAN field and clicking "Übernehmen" right after would otherwise ask the backend twice for the same values.
  const pendingBankCheckRef = useRef<{ key: string; promise: Promise<BankDetailsValidationResponseDto | null> } | null>(null);
  // A result only counts for exactly the values it was made for – editing IBAN or BIC makes it stale.
  const currentBankCheck = bankCheck?.key === toBankKey(values.iban ?? '', values.bic ?? '') ? bankCheck.result : null;
  const isBankConfirmed = !isSepa || !validateBankDetails || (currentBankCheck?.iban === true && currentBankCheck.bic === true);

  // Same schema as the submit (plus the bank check) – the button turns ready exactly when a submit would pass.
  const isReady = schema.safeParse(values).success && isBankConfirmed;

  const [openSections, setOpenSections] = useState<CheckoutSection[]>(() => getIncompleteSections(form.getValues(), paymentMethod));
  const isOpen = (section: CheckoutSection) => openSections.includes(section);
  const openSection = (section: CheckoutSection) => setOpenSections((prev) => (prev.includes(section) ? prev : [...prev, section]));

  /** Live IBAN/BIC check like the self-checkout – on blur, on "Übernehmen" and before submitting. Resolves whether both are confirmed. */
  const checkBankDetails = async (): Promise<boolean> => {
    if (!validateBankDetails) return true;
    const { iban, bic } = form.getValues();
    if (!sepaSectionSchema.safeParse({ iban, bic }).success) return false;

    const key = toBankKey(iban, bic);
    let result = bankCheck?.key === key ? bankCheck.result : null;
    if (!result) {
      let pending = pendingBankCheckRef.current;
      if (pending?.key !== key) {
        // A rejecting callback counts like `null` – the check could not run.
        const promise: Promise<BankDetailsValidationResponseDto | null> = validateBankDetails(compactBankValue(iban), compactBankValue(bic))
          .catch(() => null)
          .finally(() => {
            if (pendingBankCheckRef.current?.promise !== promise) return;
            pendingBankCheckRef.current = null;
            setIsCheckingBank(false);
          });
        pending = { key, promise };
        pendingBankCheckRef.current = pending;
        setIsCheckingBank(true);
      }
      result = await pending.promise;
    }

    // The customer may have typed on while the check was running – a late result would replace the one for the current values.
    const { iban: currentIban, bic: currentBic } = form.getValues();
    if (!result || toBankKey(currentIban, currentBic) !== key) return false;
    setBankCheck({ key, result });

    if (!result.iban) form.setError('iban', { message: 'Ungültige IBAN' });
    if (!result.bic) form.setError('bic', { message: 'Ungültige BIC' });
    return result.iban && result.bic;
  };

  const handleBankBlur = async () => {
    const { iban, bic } = form.getValues();
    if (!iban || !bic) return;
    if (await form.trigger(['iban', 'bic'])) await checkBankDetails();
  };

  const closeSection = async (section: CheckoutSection) => {
    const isValid = await form.trigger(SECTION_FIELDS[section]);
    if (!isValid) return;
    if (section === CheckoutSection.PAYMENT && !(await checkBankDetails())) return;
    setOpenSections((prev) => prev.filter((open) => open !== section));
  };

  // A collapsed section can only hold an error if it was never opened – show its inputs so the error is visible.
  const handleInvalid = (errors: FieldErrors<OfferCheckoutFormValues>) => {
    Object.values(CheckoutSection).forEach((section) => {
      if (SECTION_FIELDS[section].some((field) => errors[field])) openSection(section);
    });
  };

  const handleValidSubmit = async (submitted: OfferCheckoutFormValues) => {
    if (!onSubmit) return;
    if (isSepa && !(await checkBankDetails())) {
      openSection(CheckoutSection.PAYMENT);
      return;
    }
    await onSubmit(toAcceptOfferRequest(submitted, paymentMethod));
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleValidSubmit, handleInvalid)} className="mx-auto flex w-full max-w-sm flex-col gap-5">
        <div className="divide-y divide-[#E4E7EC] rounded-xl border border-[#E4E7EC] bg-[#F7F7F7]">
          <CheckoutDataRow label="E-Mail" isEditing={false} summary={<p>{customer.mail}</p>} />

          <CheckoutDataRow
            label="Daten"
            isEditing={isOpen(CheckoutSection.DATA)}
            onEdit={() => openSection(CheckoutSection.DATA)}
            onDone={() => closeSection(CheckoutSection.DATA)}
            summary={
              <>
                <p>
                  {values.firstName} {values.lastName}, {values.name}
                </p>
                <p>
                  {values.street} {values.houseNumber}, {values.postCode} {values.city}, {values.country && getCountryLabel(values.country)}
                </p>
                <p className="font-normal">USt-ID {values.vatId}</p>
              </>
            }>
            <CustomerDataFields control={form.control} />
          </CheckoutDataRow>

          <CheckoutDataRow
            label="Kontakt"
            isEditing={isOpen(CheckoutSection.CONTACT)}
            onEdit={() => openSection(CheckoutSection.CONTACT)}
            onDone={() => closeSection(CheckoutSection.CONTACT)}
            summary={
              <>
                <p>{values.phone}</p>
                {values.invoiceMail && <p className="font-normal">Rechnungen an {values.invoiceMail}</p>}
              </>
            }>
            <CustomerContactFields control={form.control} />
          </CheckoutDataRow>

          <CheckoutDataRow
            label="Zahlung"
            isEditing={isSepa && isOpen(CheckoutSection.PAYMENT)}
            onEdit={isSepa ? () => openSection(CheckoutSection.PAYMENT) : undefined}
            onDone={() => closeSection(CheckoutSection.PAYMENT)}
            summary={
              <>
                <p>{PAYMENT_METHOD_LABEL[paymentMethod]}</p>
                {isSepa && values.iban && <p className="font-normal">{maskIban(values.iban)}</p>}
              </>
            }>
            <p className="text-xs font-medium text-[#333333]">{PAYMENT_METHOD_LABEL[paymentMethod]}</p>
            <SepaFields control={form.control} checkResult={currentBankCheck} isChecking={isCheckingBank} onBlur={handleBankBlur} />
          </CheckoutDataRow>
        </div>

        <StartDateSelect control={form.control} options={startDateOptions} />

        <TermsCheckbox control={form.control} />

        {isSepa && <SepaMandateCheckbox control={form.control} />}

        <div className="flex flex-col gap-2">
          <Button
            type="submit"
            disabled={isPreview || form.formState.isSubmitting}
            className={cn(
              'h-11 w-full font-bold text-white',
              isReady ? 'bg-[#5FD5DE] hover:bg-[#5FD5DE]/90' : 'bg-[#082729]/50 hover:bg-[#082729]/50'
            )}>
            {form.formState.isSubmitting ? 'Wird gestartet…' : 'Zahlungspflichtig starten'}
          </Button>
          {isPreview && <p className="text-center text-xs text-[#082729]/50">Vorschau – hier nimmt der Kunde das Angebot an.</p>}
        </div>
      </form>
    </Form>
  );
};

export default OfferCheckoutForm;
